import React, { useState, useMemo, useCallback, useEffect } from 'react';

import AttachmentsTable from './AttachmentsTable';
import FileUploadBox from './FileUploadBox';

import { apiClient } from 'Helpers/network';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

import FileTextIcon from 'Assets/icons/file-text.svg';

export default function AttachmentManager({ routes, channelName, acceptedFileTypes }) {
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [editingDescription, setEditingDescription] = useState(null);
    const [descriptionText, setDescriptionText] = useState('');

    const toast = useToast();

    const confirmAttachmentDeletion = useConfirmationModal({
        message: $t('Are you sure you want to delete this attachment?'),
        warning: $t('This action cannot be reversed and the attachment cannot be recovered. Do you wish to continue?'),
    });

    const attachmentsRequest = useQuery({
        queryFn: () => apiClient.get(routes.fetch),
    });

    const remainingSlots = useMemo(() => attachmentsRequest.data?.remaining_slots ?? 5, [attachmentsRequest.data]);
    const maxFiles = useMemo(() => attachmentsRequest.data?.max_files || 5, [attachmentsRequest.data]);
    const maxFileSizeMb = useMemo(() => attachmentsRequest.data?.max_file_size_mb || 10, [attachmentsRequest.data]);

    // Subscribe to WebSocket for real-time status updates
    useEffect(() => {
        if (!channelName || !window.Echo) {
            return;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.attachment.processed', (event) => {
            if (event.attachment) {
                setAttachments((prevAttachments) =>
                    prevAttachments.map((attachment) => (attachment.id === event.attachment.id ? event.attachment : attachment)),
                );
            }
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName]);

    useEffect(() => {
        setAttachments(attachmentsRequest.data?.attachments || []);
    }, [attachmentsRequest.data]);

    const uploadFiles = useCallback(
        (files) => {
            setUploading(true);

            // Initialize progress for each file
            const initialProgress = {
                label: Array.from(files)
                    .map((file) => file.name)
                    .join(', '),
            };
            setUploadProgress(initialProgress);

            const formData = new FormData();
            files.forEach((file) => {
                formData.append('files[]', file);
                formData.append('descriptions[]', '');
            });

            return apiClient
                .post(routes.store, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress((prev) => ({ ...prev, progress: percentCompleted }));
                    },
                })
                .then(() => {
                    attachmentsRequest.refetch();
                    toast.success($t('Files uploaded successfully'));
                })
                .catch((err) => {
                    toast.error(err.response?.data?.message || $t('Failed to upload files'));
                })
                .finally(() => {
                    setUploading(false);
                    setUploadProgress(null);
                });
        },
        [attachmentsRequest, routes.store, toast],
    );

    const handleDelete = async (attachment) => {
        confirmAttachmentDeletion({
            onConfirm: async () => {
                try {
                    await apiClient.delete(routes.delete.replace('__ATTACHMENT_ID__', attachment.id));
                    attachmentsRequest.refetch();
                } catch (err) {
                    toast.error(err.response?.data?.message || $t('Failed to delete attachment'));
                }
            },
        });
    };

    const handleUpdateDescription = async (attachment) => {
        try {
            await apiClient.put(routes.update.replace('__ATTACHMENT_ID__', attachment.id), {
                description: descriptionText,
            });
            attachmentsRequest.refetch();
            setEditingDescription(null);
            setDescriptionText('');
        } catch (err) {
            toast.error(err.response?.data?.message || $t('Failed to update description'));
        }
    };

    const startEditDescription = (attachment) => {
        setEditingDescription(attachment.id);
        setDescriptionText(attachment.description || '');
    };

    const cancelEditDescription = () => {
        setEditingDescription(null);
        setDescriptionText('');
    };

    const handleDownload = (attachment) => {
        window.open(routes.download.replace('__ATTACHMENT_ID__', attachment.id), '_blank');
    };

    return (
        <div className="tw-space-y-4">
            {/* Upload section */}
            <FileUploadBox
                onFilesSelected={uploadFiles}
                accept={acceptedFileTypes}
                maxSize={maxFileSizeMb * 1024 * 1024}
                maxFiles={maxFiles}
                remainingSlots={remainingSlots}
                uploading={uploading}
                uploadProgress={uploadProgress}
                multiple
            />

            {/* Attachments list */}
            {(attachments.length > 0 || attachmentsRequest.loading) && (
                <AttachmentsTable
                    attachments={attachments}
                    isLoading={attachmentsRequest.loading}
                    editingDescription={editingDescription}
                    setDescriptionText={setDescriptionText}
                    descriptionText={descriptionText}
                    startEditDescription={startEditDescription}
                    cancelEditDescription={cancelEditDescription}
                    handleUpdateDescription={handleUpdateDescription}
                    handleDownload={handleDownload}
                    handleDelete={handleDelete}
                />
            )}

            {/* Empty state */}
            {attachments.length === 0 && !attachmentsRequest.loading && (
                <div className="tw-text-center tw-py-8 tw-text-gray-500">
                    <FileTextIcon className="tw-mx-auto tw-h-12 tw-w-12 tw-mb-4 tw-text-gray-400" />
                    <p className="tw-mt-2">{$t('No attachments yet')}</p>
                    <p className="tw-text-sm">{$t('Upload files to get started')}</p>
                </div>
            )}
        </div>
    );
}
